<template>
    <Dialog v-model:open="open">
        <DialogContent class="sm:max-w-[425px]">
            <DialogHeader>
                <DialogTitle>ABR lookup</DialogTitle>
            </DialogHeader>
            <form @submit="onSubmit">
                <div class="space-y-4">
                    <FormField name="q" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>Name / ABN / ACN (Required)</FormLabel>
                            <FormControl>
                                <Text v-bind="componentField" placeholder="Search" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                </div>
                <DialogFooter>
                    <Button type="button" variant="secondary" :disabled="searching" @click="close"
                        >Cancel</Button
                    >
                    <Button
                        type="submit"
                        :loading="searching"
                        :disabled="meta.valid === false || meta.dirty === false"
                        >Search</Button
                    >
                </DialogFooter>
            </form>
            <ABRLookupResultsModal v-model="openResultsModal" :abr="results" />
        </DialogContent>
    </Dialog>
</template>

<script setup lang="ts">
import ABRLookupResultsModal from "@/components/sidebar/modals/ABRLookupResultsModal.vue";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import Text from "@/components/ui/input/Text.vue";
import { useToast } from "@/components/ui/toast";
import { defaultGraphQLClient } from "@/composables/default-urql-client";
import { toTypedSchema } from "@vee-validate/zod";
import gql from "graphql-tag";
import { useForm } from "vee-validate";
import { ref } from "vue";
import * as z from "zod";

const open = defineModel();
const openResultsModal = ref(false);
const results = ref([]);

const emit = defineEmits(["success"]);

const { toast } = useToast();
const searching = ref(false);

const schema = z.object({
    q: z.string().min(1, {
        message: "You can't leave this empty.",
    }),
});

type Form = z.infer<typeof schema>;

const { handleSubmit, meta, setFieldError } = useForm({
    validationSchema: toTypedSchema(schema),
});

const onSubmit = handleSubmit((values) => {
    search(values);
});

function close() {
    open.value = false;
}

async function search(values: Form) {
    searching.value = true;

    try {
        const { data, error } = await defaultGraphQLClient.query(
            gql`
                query searchAbr($q: String!) {
                    abrLookup(q: $q) {
                        name
                        name_type
                        entity_name
                        entity_type_name
                        abn_status
                        acn
                        abn
                        address_postcode
                        address_state
                    }
                }
            `,
            {
                q: values.q,
            }
        );

        if (!error) {
            results.value = data.abrLookup;
            openResultsModal.value = true;
        }

        if (error) {
            const { graphQLErrors } = error;
            const first = graphQLErrors[0];

            if (first?.extensions?.category === "validation") {
                const validation = first.extensions.validation;

                if (validation && validation.q) {
                    setFieldError("q", validation.q);
                }
            }
        }
    } finally {
        searching.value = false;
    }
}
</script>
